import React from 'react';

const BrandingNeedsStep = ({ data, onChange }) => {
  const handleMaterialToggle = (material) => {
    onChange({
      ...data,
      [material]: typeof data[material] === 'boolean' ? !data[material] : true,
    });
  };

  const handleBusinessCardChange = (field, value) => {
    onChange({
      ...data,
      businessCards: {
        ...(data.businessCards || {}),
        [field]: value,
      },
    });
  };

  const handleBrochureChange = (field, value) => {
    onChange({
      ...data,
      brochures: {
        ...(data.brochures || {}),
        [field]: value,
      },
    });
  };

  const handleOtherChange = (value) => {
    onChange({
      ...data,
      other: value,
    });
  };

  return (
    <div className="onboarding-step">
      <div className="step-header">
        <h2>Branding Materials</h2>
        <p>Select the marketing materials you need for your franchise</p>
      </div>

      <div className="branding-section">
        <p className="section-note">
          All materials will be designed with Douglas & McCartney branding and personalized with your franchise details.
        </p>

        <div className="branding-grid">
          {/* Business Cards */}
          <div className="branding-item">
            <label className="branding-checkbox">
              <input
                type="checkbox"
                checked={data.businessCards?.needed || false}
                onChange={(e) => handleBusinessCardChange('needed', e.target.checked)}
              />
              <div className="branding-content">
                <div className="branding-icon">📇</div>
                <div>
                  <strong>Business Cards</strong>
                  <p>Premium quality cards with your contact details</p>
                </div>
              </div>
            </label>
            
            {data.businessCards?.needed && (
              <div className="branding-details">
                <div className="form-row">
                  <div className="form-group">
                    <label>Quantity</label>
                    <input
                      type="number"
                      value={data.businessCards?.quantity || 0}
                      onChange={(e) => handleBusinessCardChange('quantity', parseInt(e.target.value))}
                      min="0"
                      step="100"
                      placeholder="500"
                    />
                  </div>
                  <div className="form-group">
                    <label>Urgency</label>
                    <select
                      value={data.businessCards?.urgency || 'normal'}
                      onChange={(e) => handleBusinessCardChange('urgency', e.target.value)}
                    >
                      <option value="normal">Normal (2-3 weeks)</option>
                      <option value="urgent">Urgent (1 week)</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Brochures */}
          <div className="branding-item">
            <label className="branding-checkbox">
              <input
                type="checkbox"
                checked={data.brochures?.needed || false}
                onChange={(e) => handleBrochureChange('needed', e.target.checked)}
              />
              <div className="branding-content">
                <div className="branding-icon">📋</div>
                <div>
                  <strong>Brochures</strong>
                  <p>Property listing and company brochures</p>
                </div>
              </div>
            </label>
            
            {data.brochures?.needed && (
              <div className="branding-details">
                <div className="form-group">
                  <label>Quantity</label>
                  <input
                    type="number"
                    value={data.brochures?.quantity || 0}
                    onChange={(e) => handleBrochureChange('quantity', parseInt(e.target.value))}
                    min="0"
                    step="50"
                    placeholder="100"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Digital Assets */}
          <div className="branding-item">
            <label className="branding-checkbox">
              <input
                type="checkbox"
                checked={data.digitalAssets || false}
                onChange={() => handleMaterialToggle('digitalAssets')}
              />
              <div className="branding-content">
                <div className="branding-icon">🎨</div>
                <div>
                  <strong>Digital Asset Pack</strong>
                  <p>Logos, banners, and digital graphics</p>
                </div>
              </div>
            </label>
          </div>

          {/* Social Media Templates */}
          <div className="branding-item">
            <label className="branding-checkbox">
              <input
                type="checkbox"
                checked={data.socialMediaTemplates || false}
                onChange={() => handleMaterialToggle('socialMediaTemplates')}
              />
              <div className="branding-content">
                <div className="branding-icon">📱</div>
                <div>
                  <strong>Social Media Templates</strong>
                  <p>Ready-to-use posts for Instagram, Facebook, LinkedIn</p>
                </div>
              </div>
            </label>
          </div>

          {/* Email Signature */}
          <div className="branding-item">
            <label className="branding-checkbox">
              <input
                type="checkbox"
                checked={data.emailSignature || false}
                onChange={() => handleMaterialToggle('emailSignature')}
              />
              <div className="branding-content">
                <div className="branding-icon">✉️</div>
                <div>
                  <strong>Email Signature</strong>
                  <p>Professional email signature template</p>
                </div>
              </div>
            </label>
          </div>

          {/* Property Listing Templates */}
          <div className="branding-item">
            <label className="branding-checkbox">
              <input
                type="checkbox"
                checked={data.propertyListingTemplates || false}
                onChange={() => handleMaterialToggle('propertyListingTemplates')}
              />
              <div className="branding-content">
                <div className="branding-icon">🏘️</div>
                <div>
                  <strong>Property Listing Templates</strong>
                  <p>Standardized templates for property listings</p>
                </div>
              </div>
            </label>
          </div>

          {/* Letterhead */}
          <div className="branding-item">
            <label className="branding-checkbox">
              <input
                type="checkbox"
                checked={data.letterhead || false}
                onChange={() => handleMaterialToggle('letterhead')}
              />
              <div className="branding-content">
                <div className="branding-icon">📄</div>
                <div>
                  <strong>Letterhead</strong>
                  <p>Official letterhead for formal communications</p>
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Other Requirements */}
        <div className="form-group full-width">
          <label>Other Requirements</label>
          <textarea
            value={data.other || ''}
            onChange={(e) => handleOtherChange(e.target.value)}
            placeholder="Any specific branding materials or custom requirements..."
            rows="4"
          />
        </div>

        {/* Language Preference */}
        <div className="form-group">
          <label>Preferred Language for Materials</label>
          <select
            value={data.language || 'English'}
            onChange={(e) => onChange({ ...data, language: e.target.value })}
          >
            <option value="English">English</option>
            <option value="Hindi">Hindi</option>
            <option value="Both">Both (English & Hindi)</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default BrandingNeedsStep;

